"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractUserDTypesFromSpec = extractUserDTypesFromSpec;
exports.getInvalidTaskKinds = getInvalidTaskKinds;
exports.validateTaskKindAgainstSpec = validateTaskKindAgainstSpec;
exports.getValidTaskKindsForSpec = getValidTaskKindsForSpec;
/**
 * Mapping from each TaskKind to the set of user-component dtypes that must be
 * present in the task spec for that kind to be valid.
 *
 * An empty array means the kind has no specific component requirement and is
 * always considered valid (e.g. SURVEY, OTHER).
 *
 * When a kind maps to multiple dtypes, at least ONE of them must be present
 * (logical OR).
 */
const taskKindRequiredUserDTypes = {
    AUDIO_RECORDING: ['AUDIO'],
    CONVERSATION_RECORDING: ['AUDIO'],
    IMAGE_ANNOTATION: ['IMAGE_ANNOTATION'],
    IMAGE_CAPTURE: ['IMAGE'],
    ID_CAPTURE: ['USER_ID_CAPTURE'],
    SEGMENTATION: ['AUDIO_SEGMENTATION'],
    TRANSCRIPTION: ['TEXT', 'AUDIO_TRANSCRIPTION'],
    TRANSLATION: ['TEXT'],
    VALIDATION: ['BOOLEAN', 'MCQ'],
    SURVEY: [],
    EVALUATION: ['BOOLEAN', 'MCQ', 'TEXT'],
    VIDEO_RECORDING: ['VIDEO'],
    COORDINATION: [],
    CONFERENCER: [],
    OTHER: [],
};
/**
 * Extract the set of user-component dtypes present in a task spec.
 *
 * Tolerates an absent or partially-populated spec (returns an empty set) so
 * callers can run safely while the user is still composing the form.
 */
function extractUserDTypesFromSpec(spec) {
    const dataTypes = new Set();
    if (!spec)
        return dataTypes;
    for (const key of Object.keys(spec)) {
        const component = spec[key];
        if ((component === null || component === void 0 ? void 0 : component.ctype) === 'USER' && 'dtype' in component) {
            dataTypes.add(component.dtype);
        }
    }
    return dataTypes;
}
/**
 * Returns the list of TaskKind values (from the provided selection) that are
 * invalid because the spec does not contain any of their required user-component
 * dtypes.
 *
 * @param taskKinds - The task kinds selected by the user
 * @param spec      - The main task specification
 * @returns An array of invalid task kinds. Empty array means all are valid.
 */
function getInvalidTaskKinds(taskKinds, spec) {
    const presentDTypes = extractUserDTypesFromSpec(spec);
    const invalid = [];
    for (const kind of taskKinds) {
        if (!(kind in taskKindRequiredUserDTypes)) {
            // Guard against unknown kinds from external input; treat as invalid (400, not 500).
            console.error(`getInvalidTaskKinds: unknown TaskKind "${kind}"`);
            invalid.push(kind);
            continue;
        }
        const requiredDTypes = taskKindRequiredUserDTypes[kind];
        if (requiredDTypes.length === 0) {
            continue;
        }
        const hasMatch = requiredDTypes.some((dt) => presentDTypes.has(dt));
        if (!hasMatch) {
            invalid.push(kind);
        }
    }
    return invalid;
}
/**
 * Validates that the selected task kinds are consistent with the components
 * present in the task spec.
 *
 * If the spec is absent or empty, this returns `null` and defers to the spec
 * field's own required-validation, avoiding a noisy "missing components"
 * message while the user is still composing the spec.
 *
 * @returns `null` when valid, or an error message string describing the mismatch.
 */
function validateTaskKindAgainstSpec(taskKinds, spec) {
    if (!spec || Object.keys(spec).length === 0) {
        return null;
    }
    const invalidKinds = getInvalidTaskKinds(taskKinds, spec);
    if (invalidKinds.length === 0) {
        return null;
    }
    const kindNames = invalidKinds.join(', ');
    return `Spec missing required components for: ${kindNames}`;
}
/**
 * Given a task spec, returns the set of TaskKind values that are valid for it.
 * Useful for populating a filtered dropdown in the task creation form.
 */
function getValidTaskKindsForSpec(spec) {
    const allKinds = Object.keys(taskKindRequiredUserDTypes);
    const presentDTypes = extractUserDTypesFromSpec(spec);
    return allKinds.filter((kind) => {
        const requiredDTypes = taskKindRequiredUserDTypes[kind];
        if (requiredDTypes.length === 0)
            return true;
        return requiredDTypes.some((dt) => presentDTypes.has(dt));
    });
}
//# sourceMappingURL=TaskKindValidation.js.map